import {useState} from 'react';

import {SaveButton} from 'shells/button/save-button/save-button';
import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {GetEmailSyncPotentialContacts_getEmailSyncPotentialContacts as EmailSyncFoundContact} from 'nutshell-graphql-types';

import {useContactsCreateFromEmailSync} from '../graphql/hooks/use-contacts-create-from-email-sync';
import {useGetEmailSyncPotentialContacts} from '../graphql/hooks/use-get-email-sync-potential-contacts';

import {ContactsList} from './contacts-list';
import type {EmailCalendarSyncSettings} from './email-calendar-sync-settings-page';

import styles from './email-calendar-sync-contacts-page.module.css';

type Props = {
    settings: EmailCalendarSyncSettings;
    onSave: (settings: EmailCalendarSyncSettings) => Promise<void>;
    onSaveSuccess: () => void;
};

export function EmailCalendarSyncContactsPage(props: Props) {
    const [isSavingWithoutContacts, setIsSavingWithoutContacts] = useState<boolean>(false);
    const [isSavingWithContacts, setIsSavingWithContacts] = useState<boolean>(false);
    const [selectedContacts, setSelectedContacts] = useState<EmailSyncFoundContact[]>([]);

    const {contacts, isLoading} = useGetEmailSyncPotentialContacts();
    const {contactsCreateFromEmailSync} = useContactsCreateFromEmailSync();

    const hasNoContacts = !isLoading && contacts.length === 0;

    const handleSaveBase = (response: Promise<void>) => {
        return response
            .then(() => props.onSave(props.settings))
            .then(() => props.onSaveSuccess())
            .catch(() => {
                // Error is surfaced by Apollo's error state
            });
    };

    const handleSaveWithoutContacts = () => {
        setIsSavingWithoutContacts(true);

        return handleSaveBase(Promise.resolve()).finally(() => {
            setIsSavingWithoutContacts(false);
        });
    };

    const handleSaveWithContacts = () => {
        setIsSavingWithContacts(true);

        const contactsToImport = selectedContacts.map((contact) => ({
            emailAddress: {
                address: contact.emailAddress.address,
                display: contact.emailAddress.display,
            },
        }));
        const response = contactsToImport.length
            ? contactsCreateFromEmailSync(contactsToImport)
            : Promise.resolve();

        return handleSaveBase(response).finally(() => {
            setIsSavingWithContacts(false);
        });
    };

    const isDisabled =
        !props.settings.calendarId || isLoading || isSavingWithoutContacts || isSavingWithContacts;

    return (
        <div className='flex flex-dir-col'>
            <div className='pad-32'>
                <Stack spacing={24}>
                    <Body size='xlarge' isBold={true} color='dark'>
                        Want a head start?
                    </Body>
                    {!isLoading && (
                        <Body size='normal' color='dark'>
                            {hasNoContacts
                                ? 'We didn’t find any contacts in your recently sent mail.'
                                : `We found ${contacts.length} recent contacts you may want to add to Nutshell. Review and select who to add.`}
                        </Body>
                    )}
                </Stack>
            </div>
            {hasNoContacts ? (
                <>
                    <div className='flex justify-center pad-32'>
                        <Illustration src='email-sync-empty-state' width={147} height={118} />
                    </div>
                    <div className='flex justify-end pad-32'>
                        <SaveButton
                            variant='primary'
                            size='big'
                            onClick={handleSaveWithoutContacts}
                            disabled={isDisabled}
                            isSaving={isSavingWithoutContacts}
                            buttonText='Continue'
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className={styles['contact-list']}>
                        <ContactsList
                            contacts={contacts}
                            isLoading={isLoading}
                            setSelectedContacts={setSelectedContacts}
                        />
                    </div>
                    <div className='flex justify-end pad-32'>
                        <Stack isHorizontal={true} spacing={16}>
                            <SaveButton
                                variant='secondary'
                                size='big'
                                onClick={handleSaveWithoutContacts}
                                disabled={isDisabled}
                                isSaving={isSavingWithoutContacts}
                                buttonText='Don’t import anybody'
                            />
                            <div className={styles['import-people-button-wrap']}>
                                <SaveButton
                                    variant='primary'
                                    size='big'
                                    onClick={handleSaveWithContacts}
                                    disabled={isDisabled}
                                    isSaving={isSavingWithContacts}
                                    buttonText={`Import ${selectedContacts.length} people`}
                                    savingText='Importing...'
                                    successText='Imported!'
                                />
                            </div>
                        </Stack>
                    </div>
                </>
            )}
        </div>
    );
}
